// Copyright (c) StrangeBee 2023

import React, { useEffect, useMemo, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { ProviderDrawerProps } from './ProviderDrawerTypes';
import lodash from 'lodash';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';

import './ProviderDrawer.scss';

const nameKey = {
    local: {
        passwordPolicy: {
            enabled: ['enabled'],
            minLength: ['minLength'],
            minLowerCase: ['minLowerCase'],
            minUpperCase: ['minUpperCase'],
            minDigit: ['minDigit'],
            minSpecial: ['minSpecial'],
            cannotContainUsername: ['cannotContainUsername'],
        },
        maxAttempts: ['maxAttempts'],
        resetAfter: ['resetAfter'],
    },
};

export const resetFields = {
    enabled: false,
    minLength: '',
    minLowerCase: '',
    minUpperCase: '',
    minDigit: '',
    minSpecial: '',
    cannotContainUsername: false,
};

const LocalDrawer = ({
    type,
    title = type,
    enableList,
    getData,
    defaultConfig,
    onSubmit,
    triggerElem,
    availableProviders,
    className,
}: ProviderDrawerProps): ReactElement => {
    const local = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const dataCurrent = useMemo(() => getData(type), [getData, type]);

    const notAvailable = !availableProviders?.includes(type);

    const [initialValues, setInitialeValues] = useState<Record<string, any>>({});

    const [showForm, setShowForm] = useState<boolean>(false);

    const [disabledSwitch, setDisabledSwitch] = useState(true);

    const [showConfigPolicy, setShowConfigPolicy] = useState(dataCurrent?.passwordPolicy?.enabled);

    const onChangePolicySwitch = (value: boolean) => {
        setShowConfigPolicy(value);
        if (value && !initialValues.enabled && defaultConfig?.local) {
            form.setFieldsValue({ ...defaultConfig?.local.passwordPolicy, enabled: value });
        }
    };

    const computeOnFinishHandler = (toggleDrawer: any) => (state: any) => {
        const { UIenabled, maxAttempts, resetAfter, ...passwordPolicy } = state;

        const result = { ...dataCurrent, UIType: type, UIenabled, passwordPolicy, maxAttempts, resetAfter };

        onResetForm();
        toggleDrawer();
        onSubmit(result);
    };

    const onChangeSwitch = (value: any): void => {
        setShowForm(value);
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        initForm(enableList);
        setShowConfigPolicy(dataCurrent?.passwordPolicy?.enabled);
        form.resetFields();
    };

    const initForm = (enableList: string[]) => {
        const enabledLocal = enableList.includes(type);
        setShowForm(enabledLocal);
    };

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [initialValues]); //eslint-disable-line

    useEffect(() => {
        const enabledLocal = enableList.includes(type);
        const { passwordPolicy = {}, maxAttempts, resetAfter } = dataCurrent;
        setShowForm(enabledLocal);
        setShowConfigPolicy(passwordPolicy?.enabled);
        setInitialeValues({
            ...resetFields,
            ...lodash.cloneDeep(passwordPolicy),
            ...{ UIType: type, UIenabled: enabledLocal, maxAttempts, resetAfter },
        });
    }, [dataCurrent, enableList]); //eslint-disable-line

    useEffect(() => {
        initForm(enableList);
        setDisabledSwitch(!enableList.some((v) => ['oauth2', 'ad', 'ldap', 'sso'].includes(v)));
        // eslint-disable-next-line
    }, [enableList]);

    return (
        <THDrawer
            title={title}
            className={classList('auth-details__providers-drawer', className)}
            triggerElem={triggerElem}
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                initForm(enableList);
                form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    onResetForm();
                                    setFieldsTouched(false);
                                    toggleDrawer();
                                }}
                                hidden={!fieldsTouched || notAvailable}
                            />
                        }
                    >
                        <THForm.Item
                            name="UIenabled"
                            label={local('configPlateform.local.enable')}
                            valuePropName="checked"
                        >
                            <THSwitch onChange={onChangeSwitch} disabled={disabledSwitch || notAvailable} />
                        </THForm.Item>

                        {showForm && (
                            <div className="auth-form__section">
                                <div className="form__section">
                                    <TitleSection>{local('configPlateform.general.lock')}</TitleSection>
                                    <THForm.Item
                                        name={nameKey.local.maxAttempts}
                                        label={local('configPlateform.local.maxAttempts')}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.resetAfter}
                                        label={local('configPlateform.local.resetAfter')}
                                    >
                                        <THNumberSelect
                                            options={['minutes', 'hours', 'days']}
                                            disabled={notAvailable}
                                            min={0}
                                        />
                                    </THForm.Item>
                                </div>
                                <div className="form__section">
                                    <TitleSection>{local('configPlateform.general.setting')}</TitleSection>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.enabled}
                                        label={local('configPlateform.local.passwordPolicyenabled')}
                                        valuePropName="checked"
                                    >
                                        <THSwitch onChange={onChangePolicySwitch} disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.minLength}
                                        label={local('configPlateform.local.minLength')}
                                        hidden={!showConfigPolicy}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.minLowerCase}
                                        label={local('configPlateform.local.minLowerCase')}
                                        hidden={!showConfigPolicy}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.minUpperCase}
                                        label={local('configPlateform.local.minUpperCase')}
                                        hidden={!showConfigPolicy}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.minDigit}
                                        label={local('configPlateform.local.minDigit')}
                                        hidden={!showConfigPolicy}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.minSpecial}
                                        label={local('configPlateform.local.minSpecial')}
                                        hidden={!showConfigPolicy}
                                    >
                                        <THInputNumber disabled={notAvailable} min={0} />
                                    </THForm.Item>

                                    <THForm.Item
                                        name={nameKey.local.passwordPolicy.cannotContainUsername}
                                        label={local('configPlateform.local.cannotContainUsername')}
                                        valuePropName="checked"
                                        hidden={!showConfigPolicy}
                                    >
                                        <THSwitch disabled={notAvailable} />
                                    </THForm.Item>
                                </div>
                            </div>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default LocalDrawer;
